import isNotNullish from '@watershed/shared-util/src/isNotNullish';

import {
  FundAssetClass,
  SCOPE_3_SUBSCOPES,
} from '@watershed/constants/finance';
import { SCOPE_TO_OVERRIDE_FIELD_MAP } from './scopeToOverrideFieldMap';
import type { AssetYearFieldsTemp } from '../types/FinanceFragments';

/**
 * Classifies how much of an asset-year's estimated emissions are covered by
 * user-entered overrides.
 *
 * - `'none'`: no overrides set (or source is not estimation-based)
 * - `'partial'`: some but not all applicable scopes have overrides
 * - `'only-overrides'`: all applicable scopes have overrides
 */
export type ScopeOverrideCoverage = 'none' | 'partial' | 'only-overrides';

/**
 * Whether the asset class supports scope 3 overrides. Used to determine the
 * "fully overridden" threshold in {@link getScopeOverrideCoverageForEstimations}.
 */
export const ASSET_CLASS_HAS_SCOPE_3: Record<FundAssetClass, boolean> = {
  [FundAssetClass.ListedEquity]: true,
  [FundAssetClass.CorporateBonds]: true,
  [FundAssetClass.UnlistedEquity]: true,
  [FundAssetClass.BusinessLoans]: true,
  [FundAssetClass.ProjectFinance]: true,
  [FundAssetClass.CommercialLineOfInsurance]: true,
  [FundAssetClass.CommercialRealEstate]: true,
  [FundAssetClass.Mortgages]: true,
  [FundAssetClass.PersonalMotorVehicleInsurance]: false,
  [FundAssetClass.AssetGroup]: false,
  [FundAssetClass.SovereignDebt]: false,
};

// The non-null scope 3 subscope override fields, derived from SCOPE_TO_OVERRIDE_FIELD_MAP.
const SCOPE_3_SUBSCOPE_OVERRIDE_FIELDS = SCOPE_3_SUBSCOPES.map((scope) =>
  SCOPE_TO_OVERRIDE_FIELD_MAP.get(scope)
).filter(isNotNullish);

function hasAnyScopeOverride(fields: AssetYearFieldsTemp): boolean {
  return (
    fields.scope1OverrideTco2e !== null ||
    fields.scope2LocationOverrideTco2e !== null ||
    fields.scope2MarketOverrideTco2e !== null ||
    fields.scope3OverrideTco2e !== null ||
    SCOPE_3_SUBSCOPE_OVERRIDE_FIELDS.some((field) => fields[field] !== null)
  );
}

/**
 * Scope 3 is fully overridden when the overall scope 3 override is set,
 * OR every individual subscope (301–315) has an override.
 */
function isScope3FullyOverridden(fields: AssetYearFieldsTemp): boolean {
  if (fields.scope3OverrideTco2e !== null) return true;
  return SCOPE_3_SUBSCOPE_OVERRIDE_FIELDS.every(
    (field) => fields[field] !== null
  );
}

/**
 * Fully overridden means:
 * - Scope 1 is overridden
 * - Scope 2 Location OR Scope 2 Market is overridden
 * - If scope 3 is applicable: scope 3 overall OR all subscopes are overridden
 */
function isFullyOverridden(
  fields: AssetYearFieldsTemp,
  includesScope3: boolean
): boolean {
  if (fields.scope1OverrideTco2e === null) return false;
  if (
    fields.scope2LocationOverrideTco2e === null &&
    fields.scope2MarketOverrideTco2e === null
  ) {
    return false;
  }
  if (includesScope3 && !isScope3FullyOverridden(fields)) return false;
  return true;
}

/**
 * Classifies the override coverage for an asset-year.
 *
 * @param fields - The asset-year fields (from `AssetYearFieldsTemp`).
 * @param includesScope3 - Whether the asset type supports scope 3 overrides.
 *   When true, scope 3 must also be fully overridden for `'only-overrides'`.
 */
export function getScopeOverrideCoverageForEstimations({
  fields,
  includesScope3,
}: {
  fields: AssetYearFieldsTemp;
  includesScope3: boolean;
}): ScopeOverrideCoverage {
  if (!hasAnyScopeOverride(fields)) return 'none';
  if (isFullyOverridden(fields, includesScope3)) return 'only-overrides';
  return 'partial';
}
